// ProposalForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Paper,
  Divider,
  Dialog,
  DialogContent,
  FormControlLabel,
  Checkbox,
  useTheme,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { Close, Save, Add, InsertDriveFile, Delete } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import {
  PROPOSAL_STATUS,
  PROPOSAL_STATUS_LABELS,
} from "../../utils/models/proposalModel";
import { CONTRACT_STATUS } from "../../utils/models/contractModel";
import {
  ENTRY_CHANNELS,
  ENTRY_CHANNEL_LABELS,
} from "../../utils/models/clientModel";
import { CONTACT_TYPES } from "../../utils/models/contactModel";
import ClientForm from "../clients/ClientForm";
import ContactForm from "../contacts/ContactForm";

const ProposalForm = ({ proposal, onSave, onCancel }) => {
  const theme = useTheme();
  const { add: addProposal, update: updateProposal } =
    useAuditFirestore("proposals");
  const { add: addContract } = useAuditFirestore("contracts");
  const { update: updateClient } = useAuditFirestore("clients");
  const { data: clients } = useCollection("clients");
  const { data: contacts, refresh: refreshContacts } =
    useCollection("contacts");

  const { data: commercials } = useCollection("commercials");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [selectedClientAddresses, setSelectedClientAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  useEffect(() => {
    if (clients) {
      setAllClients(clients);
    }
  }, [clients]);

  const [formData, setFormData] = useState({
    title: proposal?.title || "",
    description: proposal?.description || "",
    clientId: proposal?.clientId || "",
    clientName: proposal?.clientName || "",
    clientEmail: proposal?.clientEmail || "",
    address: {
      street: proposal?.address?.street || "",
      city: proposal?.address?.city || "",
      province: proposal?.address?.province || "",
      zipCode: proposal?.address?.zipCode || "",
      country: proposal?.address?.country || "España",
    },
    entryChannel: proposal?.entryChannel || "",
    entryChannelContactId: proposal?.entryChannelContactId || "",
    commercialPercentage: proposal?.commercialPercentage || "",
    hasInvoice: proposal?.hasInvoice || false,
    invoiceLink: proposal?.invoiceLink || "",
    files: proposal?.files || [],
    version: proposal?.version || "1.0",
    versions: proposal?.versions || [],
    status: proposal?.status || PROPOSAL_STATUS.PENDING,
    notes: proposal?.notes || "",
    reminderDate: proposal?.reminderDate || "",
  });

  // Obtener nombre completo del cliente
  const getClientFullName = (client) => {
    if (!client) return "";
    return (
      client.companyName ||
      `${client.firstName || ""} ${client.lastName || ""}`.trim()
    );
  };

  // Lista de clientes activos ordenados alfabéticamente
  const sortedClients = React.useMemo(() => {
    if (!allClients) return [];
    return [...allClients]
      .filter((c) => c.isActive !== false)
      .sort((a, b) =>
        getClientFullName(a).localeCompare(getClientFullName(b), "es", {
          sensitivity: "base",
        }),
      );
  }, [allClients]);

  // Determinar si mostrar el selector de contacto de canal
  const showChannelContactSelector =
    formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL ||
    formData.entryChannel === ENTRY_CHANNELS.EPC;

  // Filtrar contactos según el canal seleccionado
  const getFilteredContacts = () => {
    if (!contacts) return [];

    if (formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL) {
      return contacts.filter(
        (c) =>
          c.contactType === CONTACT_TYPES.COMMERCIAL && c.isActive !== false,
      );
    }

    if (formData.entryChannel === ENTRY_CHANNELS.EPC) {
      return contacts.filter(
        (c) => c.contactType === CONTACT_TYPES.EPC && c.isActive !== false,
      );
    }

    return [];
  };

  // Obtener el contacto seleccionado
  const getSelectedContact = () => {
    if (!formData.entryChannelContactId || !contacts) return null;
    return (
      contacts.find((c) => c.id === formData.entryChannelContactId) || null
    );
  };

  // Resetear el contacto seleccionado cuando cambia el canal
  useEffect(() => {
    if (proposal) return;
    setFormData((prev) => ({
      ...prev,
      entryChannelContactId: "",
      commercialPercentage: "",
    }));
  }, [formData.entryChannel]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nameParts = name.split(".");

    if (nameParts.length === 2) {
      setFormData((prev) => ({
        ...prev,
        [nameParts[0]]: {
          ...prev[nameParts[0]],
          [nameParts[1]]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    if (error) setError("");
  };

  // Manejar archivos
  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }],
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleChannelContactChange = (event, newValue) => {
    if (newValue) {
      // Buscar el porcentaje del comercial seleccionado
      let percentage = "";

      // Buscar primero en contactos
      const selectedContact = contacts?.find((c) => c.id === newValue.id);
      if (selectedContact?.commercialId) {
        // Si el contacto tiene commercialId, buscar en comerciales
        const commercial = commercials?.find(
          (c) => c.id === selectedContact.commercialId,
        );
        percentage = commercial?.percentage || "";
      }

      // Si no se encontró en comerciales, buscar directamente por ID en comerciales
      if (!percentage) {
        const commercial = commercials?.find(
          (c) => c.id === newValue.id || c.contactId === newValue.id,
        );
        percentage = commercial?.percentage || "";
      }

      setFormData((prev) => ({
        ...prev,
        entryChannelContactId: newValue.id,
        commercialPercentage: percentage,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        entryChannelContactId: "",
        commercialPercentage: "",
      }));
    }
  };

  const handleContactCreated = async (newContactId) => {
    if (refreshContacts) {
      await refreshContacts();
    }

    setFormData((prev) => ({
      ...prev,
      entryChannelContactId: newContactId,
    }));

    setContactDialogOpen(false);
  };

  const handleClientSelect = (clientId) => {
    const client = allClients?.find((c) => c.id === clientId);
    if (client) {
      // Cargar direcciones del cliente
      const clientAddresses =
        client.addresses && client.addresses.length > 0
          ? client.addresses
          : [
              {
                street: client.address?.street || "",
                city: client.address?.city || "",
                state: client.address?.state || "",
                zipCode: client.address?.zipCode || "",
                country: client.address?.country || "España",
                cups: client.cups || "",
                referenciaCatastral: client.referenciaCatastral || "",
                notes: "",
                hasInvoice: client.hasInvoice || false,
                invoiceLink: client.invoiceLink || "",
              },
            ];

      setSelectedClientAddresses(clientAddresses);
      setSelectedAddressIndex(0);

      // Usar la primera dirección como predeterminada
      const primaryAddress = clientAddresses[0];

      setFormData((prev) => ({
        ...prev,
        clientId,
        clientName:
          client.companyName || `${client.firstName} ${client.lastName}`.trim(),
        clientEmail: client.email || "",
        address: {
          street: primaryAddress.street || "",
          city: primaryAddress.city || "",
          province: primaryAddress.state || primaryAddress.province || "",
          zipCode: primaryAddress.zipCode || "",
          country: primaryAddress.country || "España",
        },
        entryChannel: client.entryChannel || prev.entryChannel,
        entryChannelContactId:
          client.entryChannelContactId || prev.entryChannelContactId,
        hasInvoice: primaryAddress.hasInvoice || false,
        invoiceLink: primaryAddress.invoiceLink || "",
      }));
    }
  };

  const handleAddressSelect = (index) => {
    setSelectedAddressIndex(index);
    const address = selectedClientAddresses[index];
    if (address) {
      setFormData((prev) => ({
        ...prev,
        address: {
          street: address.street || "",
          city: address.city || "",
          province: address.state || address.province || "",
          zipCode: address.zipCode || "",
          country: address.country || "España",
        },
        hasInvoice: address.hasInvoice || false,
        invoiceLink: address.invoiceLink || "",
      }));
    }
  };

  const handleClientCreated = (newClient) => {
    setClientDialogOpen(false);

    if (newClient) {
      setAllClients((prev) => [...prev, newClient]);

      setTimeout(() => {
        const client = newClient;
        const clientAddresses =
          client.addresses && client.addresses.length > 0
            ? client.addresses
            : [
                {
                  street: client.address?.street || "",
                  city: client.address?.city || "",
                  state: client.address?.state || "",
                  zipCode: client.address?.zipCode || "",
                  country: client.address?.country || "España",
                  cups: client.cups || "",
                  referenciaCatastral: client.referenciaCatastral || "",
                  notes: "",
                  hasInvoice: client.hasInvoice || false,
                  invoiceLink: client.invoiceLink || "",
                },
              ];

        setSelectedClientAddresses(clientAddresses);
        setSelectedAddressIndex(0);

        const primaryAddress = clientAddresses[0];

        setFormData((prev) => ({
          ...prev,
          clientId: newClient.id,
          clientName:
            client.companyName ||
            `${client.firstName} ${client.lastName}`.trim(),
          clientEmail: client.email || "",
          address: {
            street: primaryAddress.street || "",
            city: primaryAddress.city || "",
            province: primaryAddress.state || primaryAddress.province || "",
            zipCode: primaryAddress.zipCode || "",
            country: primaryAddress.country || "España",
          },
          entryChannel: client.entryChannel || prev.entryChannel,
          entryChannelContactId:
            client.entryChannelContactId || prev.entryChannelContactId,
          hasInvoice: primaryAddress.hasInvoice || false,
          invoiceLink: primaryAddress.invoiceLink || "",
        }));
      }, 100);
    }
  };

  // Función para formatear el nombre del contacto en el Autocomplete
  const formatContactOption = (contact) => {
    if (!contact) return "";
    const fullName =
      `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    const email = contact.email || "";
    return email ? `${fullName} (${email})` : fullName;
  };

  // Gestión de versiones
  const handleVersionChange = () => {
    if (
      formData.status === PROPOSAL_STATUS.MODIFICATION &&
      proposal?.status !== PROPOSAL_STATUS.MODIFICATION
    ) {
      const currentVersion = parseFloat(formData.version || "1.0");
      const newVersion = (currentVersion + 0.1).toFixed(1);

      const newVersionEntry = {
        version: newVersion,
        date: new Date().toISOString(),
        previousStatus: proposal?.status || PROPOSAL_STATUS.PENDING,
        notes: `Modificación desde estado: ${PROPOSAL_STATUS_LABELS[proposal?.status] || "Pendiente"}`,
      };

      setFormData((prev) => ({
        ...prev,
        version: newVersion,
        versions: [...(prev.versions || []), newVersionEntry],
      }));
    }
  };

  useEffect(() => {
    handleVersionChange();
  }, [formData.status]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      setError("El título es obligatorio");
      return;
    }

    // Filtrar archivos vacíos
    const validFiles = formData.files.filter(
      (file) => file.name.trim() !== "" && file.url.trim() !== "",
    );

    setLoading(true);
    setError("");

    try {
      const proposalData = {
        title: formData.title,
        description: formData.description,
        clientId: formData.clientId,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        address: formData.address,
        entryChannel: formData.entryChannel,
        entryChannelContactId: formData.entryChannelContactId,
        commercialPercentage: formData.commercialPercentage,
        hasInvoice: formData.hasInvoice,
        invoiceLink: formData.invoiceLink,
        files: validFiles,
        version: formData.version,
        versions: formData.versions,
        status: formData.status,
        notes: formData.notes,
        reminderDate: formData.reminderDate,
        updatedAt: new Date().toISOString(),
      };

      const isJustAccepted =
        formData.status === PROPOSAL_STATUS.ACCEPTED &&
        proposal?.status !== PROPOSAL_STATUS.ACCEPTED;

      let savedProposalId = proposal?.id;

      if (proposal?.id) {
        await updateProposal(proposal.id, proposalData);
        savedProposalId = proposal.id;
      } else {
        savedProposalId = await addProposal({
          ...proposalData,
          version: "1.0",
          versions: [],
          createdAt: new Date().toISOString(),
          createdBy: "",
          isActive: true,
        });
      }

      // NUEVO: Sincronizar factura con el cliente (bidireccional)
      if (formData.clientId && formData.hasInvoice) {
        // Si la propuesta tiene factura, actualizar el cliente
        const client = allClients?.find((c) => c.id === formData.clientId);

        // Solo actualizar si el cliente no tiene factura o si es diferente
        if (
          !client?.hasInvoice ||
          client?.invoiceLink !== formData.invoiceLink
        ) {
          try {
            await updateClient(formData.clientId, {
              hasInvoice: true,
              invoiceLink: formData.invoiceLink || "",
              updatedAt: new Date().toISOString(),
            });
            console.log(
              "✅ Cliente actualizado con la factura de la propuesta",
            );
          } catch (err) {
            console.error("Error al actualizar cliente con factura:", err);
            // No bloqueamos el flujo si falla la actualización del cliente
          }
        }
      }

      if (
        isJustAccepted ||
        (formData.status === PROPOSAL_STATUS.ACCEPTED && !proposal?.id)
      ) {
        const client = allClients?.find((c) => c.id === formData.clientId);
        await addContract({
          title: formData.title || "Contrato sin título",
          proposalId: savedProposalId,
          proposalTitle: formData.title,
          clientId: formData.clientId,
          clientName: formData.clientName,
          clientType: client?.type || "individual",
          clientDni: client?.dni || client?.taxId || "",
          clientTaxId: client?.taxId || "",
          clientPhone: client?.phone || "",
          clientEmail: formData.clientEmail,
          description: formData.description,
          address: formData.address,
          status: CONTRACT_STATUS.PENDING,
          notes: "Contrato generado automáticamente al aceptar propuesta",
          version: "1.0",
          versions: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: "",
          isActive: true,
        });
        console.log("✅ Contrato creado automáticamente al aceptar propuesta");
      }

      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: { xs: "16px", sm: "24px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: { xs: 3, sm: 4 },
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" } }}
            >
              {proposal?.id ? "Editar propuesta" : "Nueva propuesta"}
            </Typography>
            {proposal?.version && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  display: "block",
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                }}
              >
                Versión {proposal.version}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onCancel}
            sx={{
              background: theme.palette.background.paper,
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "#e2e8f0",
            }}
          >
            <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: { xs: 2, sm: 3 },
              borderRadius: "12px",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* CLIENTE */}
          <Typography sx={sectionTitleSx}>Cliente</Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center" }}>
            <Autocomplete
              fullWidth
              size="small"
              options={sortedClients}
              value={
                sortedClients.find((c) => c.id === formData.clientId) || null
              }
              onChange={(event, newValue) => {
                handleClientSelect(newValue ? newValue.id : "");
              }}
              getOptionLabel={(option) => getClientFullName(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, { inputValue }) => {
                const search = inputValue.toLowerCase().trim();
                if (!search) return options;
                return options.filter((c) =>
                  getClientFullName(c).toLowerCase().includes(search),
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar cliente"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: theme.palette.background.paper,
                      height: { xs: 40, sm: 44 },
                    },
                    "& .MuiInputLabel-root": {
                      backgroundColor: theme.palette.background.paper,
                      paddingInline: "4px",
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                    >
                      {getClientFullName(option)}
                    </Typography>
                    {option.email && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                      >
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              noOptionsText="No se encontraron clientes"
            />
            <IconButton
              onClick={() => setClientDialogOpen(true)}
              sx={{
                borderRadius: "12px",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                minWidth: { xs: 40, sm: 44 },
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              <Add />
            </IconButton>
          </Box>

          {/* Selector de dirección si el cliente tiene varias */}
          {selectedClientAddresses.length > 1 && (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                  Seleccionar dirección del cliente
                </InputLabel>
                <Select
                  value={selectedAddressIndex}
                  onChange={(e) => handleAddressSelect(e.target.value)}
                  label="Seleccionar dirección del cliente"
                  sx={{
                    borderRadius: "12px",
                    background: theme.palette.background.paper,
                    height: { xs: 40, sm: 44 },
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  }}
                >
                  {selectedClientAddresses.map((addr, index) => (
                    <MenuItem
                      key={index}
                      value={index}
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                    >
                      {`Dirección ${index + 1}: ${addr.street || "Sin calle"}, ${addr.city || "Sin ciudad"}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* CANAL DE ENTRADA */}
          <Typography sx={sectionTitleSx}>Canal de entrada</Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
              alignItems: "flex-start",
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "300px" } }}
            >
              <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                Canal
              </InputLabel>
              <Select
                name="entryChannel"
                value={formData.entryChannel}
                onChange={handleChange}
                label="Canal"
                sx={{
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  height: { xs: 40, sm: 44 },
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                <MenuItem
                  value=""
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  Sin canal
                </MenuItem>
                {Object.entries(ENTRY_CHANNEL_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Selector de contacto de canal (Comercial o EPC) */}
            {showChannelContactSelector && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
              >
                <Autocomplete
                  value={getSelectedContact()}
                  onChange={handleChannelContactChange}
                  options={getFilteredContacts()}
                  getOptionLabel={(option) => formatContactOption(option)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL
                          ? "Seleccionar Comercial"
                          : "Seleccionar EPC"
                      }
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.paper,
                          height: { xs: 40, sm: 44 },
                        },
                        "& .MuiInputLabel-root": {
                          backgroundColor: theme.palette.background.paper,
                          paddingInline: "4px",
                          fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                        >
                          {formatContactOption(option)}
                        </Typography>
                        {option.position && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                          >
                            {option.position}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  noOptionsText={
                    formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL
                      ? "No hay comerciales disponibles"
                      : "No hay EPCs disponibles"
                  }
                  sx={{ flex: 1 }}
                />

                {/* Botón + solo para Comercial */}
                {formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL && (
                  <Tooltip title="Añadir nuevo comercial">
                    <IconButton
                      onClick={() => setContactDialogOpen(true)}
                      sx={{
                        width: { xs: 40, sm: 44 },
                        height: { xs: 40, sm: 44 },
                        borderRadius: "12px",
                        backgroundColor: theme.palette.primary.main,
                        color: "white",
                        "&:hover": {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>

          {/* Porcentaje comercial (solo cuando el canal es comercial) */}
          {formData.entryChannel === ENTRY_CHANNELS.COMMERCIAL && (
            <Box sx={{ mt: 2.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Porcentaje comercial"
                name="commercialPercentage"
                value={formData.commercialPercentage}
                onChange={handleChange}
                sx={{ ...fieldSx, maxWidth: { xs: "100%", sm: "400px" } }}
              />
            </Box>
          )}

          {/* DATOS DE LA PROPUESTA */}
          <Typography sx={sectionTitleSx}>Datos de la propuesta</Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2.5 },
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Título"
              name="title"
              value={formData.title}
              onChange={handleChange}
              sx={fieldSx}
              required
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={5}
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleChange}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  background: theme.palette.background.paper,
                },
              }}
            />
          </Box>

          {/* DIRECCIÓN */}
          <Typography sx={sectionTitleSx}>Dirección</Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2.5 },
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Calle"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Población/Ciudad"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Provincia"
                name="address.province"
                value={formData.address.province}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Código Postal"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="País"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          </Box>

          {/* FACTURA */}
          <Typography sx={sectionTitleSx}>Factura</Typography>

          <FormControlLabel
            control={
              <Checkbox
                name="hasInvoice"
                checked={formData.hasInvoice}
                onChange={handleChange}
              />
            }
            label={
              <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                ¿Tiene factura de luz?
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          {formData.hasInvoice && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: "16px",
                border: "1px solid",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.12)"
                    : "#e2e8f0",
                background: theme.palette.background.paper,
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                Factura de luz
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Enlace a la factura"
                name="invoiceLink"
                value={formData.invoiceLink}
                onChange={handleChange}
                placeholder="https://drive.google.com/..."
                helperText="Pega aquí el enlace a la factura (Google Drive, Dropbox, etc.)"
                sx={fieldSx}
                inputProps={{
                  style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                }}
              />
            </Box>
          )}

          {/* ARCHIVOS */}
          <Typography sx={sectionTitleSx}>Archivos</Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              width: "100%",
            }}
          >
            {formData.files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: "center",
                  width: "100%",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <TextField
                  size="small"
                  label="Nombre del archivo"
                  value={file.name}
                  onChange={(e) =>
                    handleFileChange(index, "name", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
                <TextField
                  size="small"
                  label="Enlace (URL)"
                  value={file.url}
                  onChange={(e) =>
                    handleFileChange(index, "url", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 2,
                    minWidth: { xs: "100%", sm: "300px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveFile(index)}
                  size="small"
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: theme.palette.error.main,
                      color: "white",
                    },
                  }}
                >
                  <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddFile}
              sx={{
                borderRadius: "12px",
                height: { xs: 40, sm: 44 },
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                alignSelf: "flex-start",
                mt: 1,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(0, 175, 163, 0.1)"
                      : "rgba(0, 95, 101, 0.05)",
                },
              }}
            >
              Añadir archivo
            </Button>
          </Box>

          {/* ESTADO Y SEGUIMIENTO */}
          <Typography sx={sectionTitleSx}>Estado y seguimiento</Typography>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "260px" } }}
            >
              <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                Estado
              </InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Estado"
                sx={{
                  borderRadius: "12px",
                  background: theme.palette.background.paper,
                  height: { xs: 40, sm: 44 },
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                <MenuItem
                  value="pending"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  Pendiente
                </MenuItem>
                <MenuItem
                  value="created"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  En proceso
                </MenuItem>
                <MenuItem
                  value="sent"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  Enviada
                </MenuItem>
                <MenuItem
                  value="modification"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  Modificación
                </MenuItem>
                <MenuItem
                  value="rejected"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  Rechazada
                </MenuItem>
                <MenuItem
                  value="accepted"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                >
                  Aceptada
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Fecha de aviso"
              name="reminderDate"
              value={formData.reminderDate}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "260px" },
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>

          {/* VERSIÓN ACTUAL */}
          {formData.version && (
            <>
              <Typography sx={sectionTitleSx}>Versión</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="primary"
                  sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                >
                  Versión actual: {formData.version}
                </Typography>
                {formData.versions?.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    ({formData.versions.length} cambios registrados)
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* NOTAS */}
          <Typography sx={sectionTitleSx}>Notas internas</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notas"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            inputProps={{
              style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                background: theme.palette.background.paper,
              },
            }}
          />

          {/* FOOTER */}
          <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={<Save />}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                boxShadow: "none",
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              {loading
                ? "Guardando..."
                : proposal?.id
                  ? "Actualizar"
                  : "Crear propuesta"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* DIÁLOGO PARA CREAR CLIENTE */}
      <Dialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ClientForm
            onSave={(newClientData) => {
              handleClientCreated(newClientData);
            }}
            onCancel={() => setClientDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* DIÁLOGO PARA CREAR CONTACTO COMERCIAL */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            maxHeight: "90vh",
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ pt: 2 }}>
          <ContactForm
            contact={{
              contactType: CONTACT_TYPES.COMMERCIAL,
            }}
            onSave={(savedContact) => {
              handleContactCreated(savedContact?.id || savedContact);
            }}
            onCancel={() => setContactDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProposalForm;
